<template>
	<div class="logs-container">
		<ErrorEmptyState
			v-if="loadError"
			title="Could not load Account Logs"
			:message="errorMessage"
			:retrying="userStore.activityLogsLoading"
			bordered
			@retry="refreshLogs"
		/>
		<template v-else>
			<div class="logs-header">
				<div class="input-group logs-time-range">
					<div class="field-label">Time Range</div>
					<v-select
						v-model="timeRange"
						:items="timeRangeOptions"
						hide-details
						:menu-icon="''"
						@update:model-value="handleTimeRangeChange"
					>
						<template #append-inner>
							<ChevronDown16 class="select-chevron" />
						</template>
					</v-select>
				</div>
				<div class="logs-summary">
					<span class="sgnl-paragraph-s-regular sgnl-text-color-secondary"
						>Last {{ totalLogCount }} logs from {{ formatDateRange() }}</span
					>
					<TooltipButton
						variant="flat"
						color="primary"
						@click="refreshLogs"
						tooltipText="Refresh logs"
						data-test="refresh-logs-button"
						:loading="userStore.activityLogsLoading"
					>
						<template #btnContent>
							<Renew16 />
							<span>Refresh</span>
						</template>
					</TooltipButton>
				</div>
			</div>
			<SgnlTable
				:columns="columns"
				:data="rows"
				:loading="userStore.activityLogsLoading"
				:empty-text="userStore.activityLogsError || noDataText"
				:card-breakpoint="959"
				paginated
				:page-size="10"
				:page-size-options="[10, 25, 50, 100]"
			>
				<template #cell-eventType="{ row }">
					<div
						class="log-event-type sgnl-paragraph-s-regular sgnl-text-color-secondary"
						:title="row.eventType"
					>
						{{ row.eventType }}
					</div>
				</template>
				<template #cell-timestamp="{ row }">
					<div
						class="sgnl-paragraph-s-regular sgnl-text-color-secondary"
						:title="formatFullTimestamp(row.timestamp)"
					>
						{{ formatCompactTimestamp(row.timestamp) }}
					</div>
				</template>
				<template #cell-eventResponse="{ row }">
					<span class="sgnl-paragraph-s-regular sgnl-text-color-secondary">{{ row.eventResponse }}</span>
				</template>
				<template #cell-location="{ row }">
					<div
						class="location-text sgnl-paragraph-s-regular sgnl-text-color-secondary"
						:title="formatLocation(row)"
					>
						{{ formatLocation(row) }}
					</div>
				</template>
				<template #cell-ipAddress="{ row }">
					<div
						class="ip-address sgnl-code-default sgnl-text-color-secondary"
						:title="row.ipAddress"
					>
						{{ formatIpAddress(row.ipAddress) || "N/A" }}
					</div>
				</template>
				<template #cell-deviceBrowser="{ row }">
					<div
						class="device-browser sgnl-paragraph-s-regular sgnl-text-color-secondary"
						:title="row.deviceName"
					>
						{{ formatBrowserName(row.deviceName) || "N/A" }}
					</div>
				</template>
				<template #cell-deviceOs="{ row }">
					<div
						class="device-os sgnl-paragraph-s-regular sgnl-text-color-secondary"
						:title="row.deviceName"
					>
						{{ formatOsName(row.deviceName) || "N/A" }}
					</div>
				</template>

				<!-- Mobile card -->
				<template #card="{ row }">
					<div class="log-card__top">
						<span
							class="log-event-type sgnl-paragraph-s-medium sgnl-text-color-default"
							:title="row.eventType"
							>{{ row.eventType }}</span
						>
						<span
							class="sgnl-paragraph-s-regular sgnl-text-color-secondary sgnl-flex-shrink-0"
							:title="formatFullTimestamp(row.timestamp)"
							>{{ formatCompactTimestamp(row.timestamp) }}</span
						>
					</div>
					<div class="log-card__meta sgnl-paragraph-s-regular sgnl-text-color-secondary">
						<span>{{ row.eventResponse }}</span>
						<span>·</span>
						<span>{{ formatLocation(row) }}</span>
						<span>·</span>
						<span class="ip-address sgnl-code-default">{{ formatIpAddress(row.ipAddress) || "N/A" }}</span>
					</div>
					<div class="log-card__meta sgnl-paragraph-s-regular sgnl-text-color-secondary">
						<span>{{ formatBrowserName(row.deviceName) || "N/A" }}</span>
						<span>·</span>
						<span>{{ formatOsName(row.deviceName) || "N/A" }}</span>
					</div>
				</template>
			</SgnlTable>
		</template>
	</div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import Renew16 from "@carbon/icons-vue/es/renew/16";
import ChevronDown16 from "@carbon/icons-vue/es/chevron--down/16";

// Types
import { AWSAuthenticatedUserObject, AccountLogItem } from "@/types/user";

// Libraries
import moment from "moment";

// Utilities
import * as Sentry from "@sentry/vue";

// Components
import TooltipButton from "@/components/Common/TooltipButton.vue";
import ErrorEmptyState from "@/components/Common/ErrorEmptyState.vue";
import SgnlTable from "@/components/Common/Table.vue";
import { type ColumnDef } from "@tanstack/vue-table";

// Stores
import { mapStores } from "pinia";
import { useUserStore } from "@/stores/user";

// Sorting is disabled; logs are pre-sorted newest-first in the `rows` computed.
const LOG_COLUMNS: ColumnDef<AccountLogItem>[] = [
	{ accessorKey: "timestamp", header: "Date", enableSorting: false },
	{ accessorKey: "eventType", header: "Event", enableSorting: false },
	{ accessorKey: "eventResponse", header: "Status", enableSorting: false },
	{ id: "location", header: "Location", enableSorting: false },
	{ accessorKey: "ipAddress", header: "IP", enableSorting: false },
	{ id: "deviceBrowser", header: "Browser", enableSorting: false },
	{ id: "deviceOs", header: "OS", enableSorting: false },
];

type ComponentData = {
	authenticatedUser: undefined | null | AWSAuthenticatedUserObject;
	noDataText: string;
	loadError: boolean;
	errorMessage: string;
	timeRange: string;
	timeRangeOptions: { title: string; value: string }[];
	queryTimeRange: { startTime: number; endTime: number };
	queryItemsLimit: number;
};

export default defineComponent({
	name: "SettingsAccountLogs",
	props: {},
	components: {
		TooltipButton,
		ErrorEmptyState,
		SgnlTable,
		Renew16,
		ChevronDown16,
	},
	data: (): ComponentData => ({
		authenticatedUser: undefined,
		noDataText: "No activity logs found for the specified time range.",
		loadError: false,
		errorMessage: "",
		timeRange: "7d",
		timeRangeOptions: [
			{ title: "Last 24 hours", value: "1d" },
			{ title: "Last 3 days", value: "3d" },
			{ title: "Last week", value: "7d" },
			{ title: "Last month", value: "30d" },
		],
		queryTimeRange: {
			startTime: 0,
			endTime: 0,
		},
		queryItemsLimit: 200,
	}),
	computed: {
		...mapStores(useUserStore),
		columns(): ColumnDef<AccountLogItem>[] {
			return LOG_COLUMNS;
		},
		allLogs(): AccountLogItem[] {
			return this.userStore?.currentUserActivityLogs?.logs || [];
		},
		// Newest logs first; the table itself does not offer interactive sorting.
		rows(): AccountLogItem[] {
			return [...this.allLogs].sort((a, b) => moment(b.timestamp).valueOf() - moment(a.timestamp).valueOf());
		},
		totalLogCount(): number {
			return this.allLogs.length;
		},
	},
	methods: {
		async refreshLogs() {
			this.loadError = false;
			try {
				if (!this.authenticatedUser || !this.authenticatedUser.username) {
					throw new Error("User not authenticated");
				}

				const endTime = Date.now();
				let startTime = endTime;

				switch (this.timeRange) {
					case "1d":
						startTime = endTime - 24 * 60 * 60 * 1000;
						break;
					case "3d":
						startTime = endTime - 3 * 24 * 60 * 60 * 1000;
						break;
					case "7d":
						startTime = endTime - 7 * 24 * 60 * 60 * 1000;
						break;
					case "30d":
						startTime = endTime - 30 * 24 * 60 * 60 * 1000;
						break;
					default:
						startTime = endTime - 7 * 24 * 60 * 60 * 1000;
				}

				const result = await this.userStore.fetchCurrentUserActivityLogs({
					startTime,
					endTime,
					limit: this.queryItemsLimit,
				});

				if (!result) {
					this.errorMessage = "Often this is a temporary network or connectivity issue.";
					this.loadError = true;
					return;
				}

				this.queryTimeRange = this.userStore.currentUserActivityLogs?.queryTimeRange || {
					startTime: 0,
					endTime: 0,
				};
			} catch (error) {
				this.errorMessage = "Often this is a temporary network or connectivity issue.";
				this.loadError = true;
				console.error("Error retrieving user logs:", error);
				Sentry.captureException(error, {
					level: "error",
					tags: { service: "user-logs" },
				});
			}
		},
		handleTimeRangeChange() {
			this.refreshLogs();
		},
		formatFullTimestamp(timestamp: string): string {
			return moment(timestamp).format("MMM DD, YYYY HH:mm:ss");
		},
		formatCompactTimestamp(timestamp: string): string {
			const date = moment(timestamp);
			const today = moment().startOf("day");

			if (date.isSame(today, "day")) {
				return date.format("HH:mm");
			} else {
				return date.format("MM/DD HH:mm");
			}
		},
		formatLocation(item: AccountLogItem): string {
			if (item.city && item.country) {
				// Just return city if names are long
				if (item.city.length + item.country.length > 12) {
					return item.city;
				}
				return `${item.city}, ${item.country}`;
			} else if (item.city) {
				return item.city;
			} else if (item.country) {
				return item.country;
			}
			return "Unknown";
		},
		formatIpAddress(ipAddress: string | undefined): string {
			if (!ipAddress) return "";

			if (ipAddress.includes(".")) {
				return ipAddress;
			}

			if (ipAddress.includes(":")) {
				const parts = ipAddress.split(":");
				return `${parts[0]}:...`;
			}

			return ipAddress;
		},
		formatBrowserName(deviceName: string | undefined): string {
			if (!deviceName) return "";

			const parts = deviceName.split(",");
			if (parts.length > 0) {
				const browserPart = parts[0].trim();
				return browserPart;
			}

			return "";
		},
		formatOsName(deviceName: string | undefined): string {
			if (!deviceName) return "";

			const parts = deviceName.split(",");
			if (parts.length > 1) {
				return parts[1].trim();
			}

			return "";
		},
		formatDateRange(): string {
			if (this.queryTimeRange.startTime && this.queryTimeRange.endTime) {
				const start = moment(this.queryTimeRange.startTime).format("MMM DD");
				const end = moment(this.queryTimeRange.endTime).format("MMM DD, YYYY");
				return `${start} - ${end}`;
			}
			return "";
		},
	},
	async created() {
		const authUser = await this.userStore.getCurrentAuthenticatedUserObject();
		this.authenticatedUser = authUser;

		await this.refreshLogs();
	},
});
</script>

<style lang="css" scoped>
.logs-container {
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-16);
}

.logs-header {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-12);
}

.logs-time-range {
	width: 100%;
}

.logs-summary {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: var(--sgnl-space-8);
}

@media (min-width: 600px) {
	.logs-header {
		flex-direction: row;
		align-items: flex-end;
		justify-content: space-between;
	}

	.logs-time-range {
		width: var(--sgnl-container-343);
	}

	.logs-summary {
		flex-direction: row;
		align-items: center;
	}
}

/* Column widths target the table cells rendered inside the Table wrapper. */
:deep(.col-timestamp) {
	width: 90px;
}

:deep(.col-eventType) {
	width: 70px;
}

:deep(.col-eventResponse) {
	width: 80px;
}

:deep(.col-location) {
	width: 70px;
}

:deep(.col-ipAddress) {
	width: 100px;
}

:deep(.col-deviceBrowser) {
	width: 80px;
}

:deep(.col-deviceOs) {
	width: 80px;
}

.log-event-type,
.device-browser,
.device-os,
.location-text {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 100%;
}

.ip-address {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 100%;
}

/* === Mobile card sub-layout === */
.log-card__top {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: var(--sgnl-space-8);
}

.log-card__top .log-event-type {
	min-width: 0;
	flex: 1;
}

.log-card__meta {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: var(--sgnl-space-8);
}
</style>
